import { config as translationsConfig } from '@mfw/translations';
import { pickPrimary } from '@mfw/common';

import type {
  ApiOrganizationClient,
  UiOrganizationClient,
} from '../../schemas/organizationUser/organization.client.schema';
import type { Maybe } from '../../types/maybe.types';

import {
  mapUserAddressCountryFromApi,
  mapFitnessProfileFromApi,
  mapUserSettingsFromApi,
  mapUserRolesFromApi,
  mapValue,
} from './users.mappers';
import { mergeDefaultSocialMedia } from './socialMedia.mappers';

export function mapOrganizationClientFromApi(
  apiUser: Maybe<ApiOrganizationClient>,
): Maybe<UiOrganizationClient> {
  const primaryAddress = pickPrimary(apiUser?.addresses);
  const primaryContact = pickPrimary(apiUser?.contacts);
  return {
    id: apiUser?.id ?? '',
    address: primaryAddress
      ? {
          city: mapValue(primaryAddress?.city),
          country: mapUserAddressCountryFromApi(primaryAddress?.country),
          isAddressPublic: mapValue(primaryAddress?.isAddressPublic),
          state: mapValue(primaryAddress?.state),
          street: mapValue(primaryAddress?.street),
          zip: mapValue(primaryAddress?.zip),
        }
      : undefined,
    clientAccess: apiUser?.clientAccess ?? null,
    clientCanRestore: apiUser?.clientCanRestore ?? false,
    clientMoneyVisible: apiUser?.clientMoneyVisible ?? false,
    clientGroups:
      apiUser?.clientGroups?.map((g) => ({
        id: g.id,
        color: g.color,
        name: g.name,
      })) ?? [],
    connectedAt: mapValue(apiUser?.connectedAt),
    contact: primaryContact
      ? {
          phoneCountryCode:
            mapValue(primaryContact?.phoneCountryCode) ??
            translationsConfig.defaultCountryCode,
          phoneNumber: mapValue(primaryContact?.phoneNumber) ?? '',
        }
      : undefined,
    createdAt: mapValue(apiUser?.createdAt),
    email: mapValue(apiUser?.email),
    fitnessProfile: mapFitnessProfileFromApi(apiUser?.fitnessProfiles),
    memberships:
      apiUser?.memberships?.map((m) => ({
        id: m.id,
        startDate: m.startDate,
        endDate: m.endDate,
        membershipId: m.membershipId,
        name: m.name,
        status: m.status,
      })) ?? [],
    memberNumber: apiUser?.memberNumber ?? null,
    name: mapValue(apiUser?.name),
    notPaidCurrency: apiUser?.notPaidCurrency ?? null,
    notPaidDueMinor: apiUser?.notPaidDueMinor ?? null,
    notPaidRenewed: apiUser?.notPaidRenewed ?? false,
    orgUserId: mapValue(apiUser?.orgUserId),
    // absent stays absent: the server omits it when the viewer has no money
    // right, and `[]` would read as a real "paid nothing"
    paymentsByCurrency: apiUser?.paymentsByCurrency ?? undefined,
    paymentsCount: apiUser?.paymentsCount ?? null,
    checkInsUsed: apiUser?.checkInsUsed ?? null,
    checkInsLimit: apiUser?.checkInsLimit ?? null,
    renewalNotification: apiUser?.renewalNotification ?? null,
    personalTrainerOrganizationId: undefined,
    profileUrl: undefined,
    roles: mapUserRolesFromApi(mapValue(apiUser?.roles)) ?? [],
    settings: apiUser?.settings
      ? mapUserSettingsFromApi(apiUser?.settings)
      : undefined,
    socialMedia: apiUser?.socialMedia
      ? mergeDefaultSocialMedia(apiUser?.socialMedia)
      : undefined,
    status: mapValue(apiUser?.orgStatus),
    trainers:
      apiUser?.trainers?.map((trainer) => ({
        id: trainer.id,
        name: mapValue(trainer.name),
        settings: trainer.settings
          ? { profileUrl: mapValue(trainer.settings.profileUrl) }
          : undefined,
      })) ?? [],
  };
}
